import { booleanForComponentWithId } from "./mapper/api-mappers";

/**
 * Herreapoteket and Dameapoteket are two deployments reading the same
 * Crystallize tenant, so editorial content (stories) lives in one shared tree.
 * Which shop a story appears in is decided by one boolean ("Switch") component
 * per shop on the `story` shape.
 *
 * `store` is the Herreapoteket toggle - it is named "store" rather than
 * "herreapoteket" because it predates the second shop and the production
 * tenant already has stories tagged with it. This map is the single place that
 * knows about it.
 */
export const SHOP_VISIBILITY_COMPONENT_ID = {
  herreapoteket: "store",
  dameapoteket: "store-dameapoteket",
} as const;

export type ShopName = keyof typeof SHOP_VISIBILITY_COMPONENT_ID;

export function currentShopName(): ShopName {
  return process.env.SHOP_NAME === "dameapoteket"
    ? "dameapoteket"
    : "herreapoteket";
}

export function isPathForCurrentShop(path: string): boolean {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (currentShopName() === "herreapoteket") {
    return !normalized.startsWith("/dameapoteket");
  }
  return true;
}

/**
 * Shop visibility for shared-tenant stories.
 *
 * Editors turn a shop switch *on* for shops that should show the story; they
 * rarely turn switches off. So:
 * - no shop toggles set → visible everywhere (legacy / untagged)
 * - any shop toggle set → visible only where this shop's toggle is explicitly true
 *
 * Products/folders without these components stay visible.
 */
export function isVisibleInCurrentShop(components: any[] | undefined): boolean {
  const herre = booleanForComponentWithId(
    components,
    SHOP_VISIBILITY_COMPONENT_ID.herreapoteket,
  );
  const dame = booleanForComponentWithId(
    components,
    SHOP_VISIBILITY_COMPONENT_ID.dameapoteket,
  );

  if (herre === undefined && dame === undefined) {
    return true;
  }

  const mine =
    currentShopName() === "dameapoteket" ? dame : herre;
  return mine === true;
}
